import { daysApart } from "@bl/domain";
import type {
  BrewSummary,
  InvoiceSummary,
  Item,
  Location,
  RecipeDraftSummary,
  RecipeSummary,
  StockImportSummary,
} from "./api";
import { categoryLabel, formLabel, regionText, showDate, today } from "./format";

/**
 * Search, shared by the header's search of everything (Tyler, 2026-10-03: "full omni search") and the
 * search across the top of Inventory. Case and accents aside, every word typed has to be found in what
 * is searched, so "citra cryo" finds Citra in Cryo. A match at the start of the name ranks first, then
 * one at the start of a word in the name, then one anywhere in the name, then one only in what else the
 * thing is known by.
 */

/** Lower case with accents dropped: "Château" and "chateau" are one search. */
export function fold(text: string): string {
  return text.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
}

/** The words of a search, folded. */
export function wordsOf(query: string): string[] {
  return fold(query).split(/\s+/).filter(Boolean);
}

/**
 * How well a thing answers a search: 0 when some word is found neither in its name nor in anything
 * else it is known by, and 1 to 4 otherwise, the higher the better.
 */
export function score(
  words: string[],
  name: string,
  also: readonly (string | null | undefined)[] = [],
): number {
  if (words.length === 0) return 0;
  const n = fold(name);
  // A character no one types keeps one field's end from running into the next one's start.
  const all = [n, ...also.filter((s): s is string => Boolean(s)).map(fold)].join("\u0000");
  if (!words.every((w) => all.includes(w))) return 0;
  if (n.startsWith(words.join(" "))) return 4;
  const nameWords = n.split(/[^\p{L}\p{N}]+/u);
  if (words.every((w) => nameWords.some((nw) => nw.startsWith(w)))) return 3;
  if (words.every((w) => n.includes(w))) return 2;
  return 1;
}

/** Everything an item is known by besides its name: its form, maker, brands, kind, region and SKU. */
export function itemTerms(i: Item): string[] {
  return [
    i.name,
    formLabel(i),
    i.manufacturer ?? "",
    ...i.onHand.brands,
    categoryLabel(i.category),
    regionText(i),
    i.hopVariety ?? "",
    i.defaultSupplier?.name ?? "",
    i.defaultSupplierSku ?? "",
  ];
}

/** Whether an item answers a search on Inventory; an empty search answers to everything. */
export function itemMatches(i: Item, query: string): boolean {
  const words = wordsOf(query);
  return words.length === 0 || score(words, i.displayName, itemTerms(i)) > 0;
}

/* ---------- Everything ---------- */

/** The kinds of thing found, in the order the results list them. */
export const KINDS = [
  { kind: "page", label: "Pages" },
  { kind: "item", label: "Inventory" },
  { kind: "recipe", label: "Recipes" },
  { kind: "brew", label: "Brews" },
  { kind: "invoice", label: "Invoices" },
  { kind: "location", label: "Locations" },
  { kind: "brewery", label: "Borrowing" },
  { kind: "brewSystem", label: "Brew systems" },
  { kind: "draft", label: "Unsaved drafts" },
  { kind: "ollie", label: "Ollie reports" },
] as const;
export type Kind = (typeof KINDS)[number]["kind"];

export interface Hit {
  /** Unique among the results. */
  key: string;
  kind: Kind;
  title: string;
  /** A line beside the title that tells two alike apart: a recipe's style, an invoice's date. */
  detail?: string;
  /** Where it opens. */
  href: string;
  /** A location opens Inventory on what is in it, as a click on the Locations page does. */
  locationId?: string;
  score: number;
  /** Among things matched as well, which comes first: lower first. */
  order?: number;
}

/** A page of the app, or a part of one, by the name its heading gives it. */
export interface PageEntry {
  label: string;
  href: string;
  /** Where it is, when that is not plain from its name: "Settings". */
  detail?: string;
  /** Other words someone might look for it by: Calendar is the schedule. */
  keywords?: string[];
}

/** What the header can search. A list not loaded yet is left out. */
export interface Sources {
  pages: PageEntry[];
  items?: Item[];
  recipes?: RecipeSummary[];
  brews?: BrewSummary[];
  invoices?: InvoiceSummary[];
  locations?: Location[];
  /** The breweries stock is lent to or borrowed from. */
  breweries?: string[];
  brewSystems?: { id: string; name: string }[];
  drafts?: RecipeDraftSummary[];
  ollieReports?: StockImportSummary[];
}

/** At most this many of each kind, so one long list does not push every other kind out of sight. */
export const PER_KIND = 5;

const INVOICE_STATUS: Record<InvoiceSummary["status"], string> = {
  draft: "To review",
  committed: "Received",
  discarded: "Discarded",
};

/**
 * Brews of one recipe all match its name alike, so the one in progress comes first, then the planned
 * ones soonest first, then the brewed ones latest first (the re-critique, 2026-10-07: "pale" listed
 * five planned for April 2027 ahead of the one in progress and the one in FV2).
 */
function brewOrder(b: BrewSummary, now: string): number {
  if (b.status === "draft") return 0;
  const days = daysApart(now, b.brewedOn);
  return b.status === "planned" ? 1 + Math.abs(days) : 1_000_000 - days;
}

function brewDetail(b: BrewSummary): string {
  if (b.status === "planned") return `Planned for ${showDate(b.brewedOn)}`;
  if (b.status === "draft") return `In progress, ${showDate(b.brewedOn)}`;
  return `Brewed ${showDate(b.brewedOn)}`;
}

/**
 * Everything that answers a search, best first within each kind and at most `PER_KIND` of each, the
 * kinds in the order of `KINDS`.
 */
export function searchEverything(query: string, s: Sources): Hit[] {
  const words = wordsOf(query);
  if (words.length === 0) return [];
  const hits: Hit[] = [];
  const add = (
    kind: Kind,
    key: string,
    title: string,
    also: (string | null | undefined)[],
    rest: Pick<Hit, "href" | "detail" | "locationId" | "order">,
  ) => {
    const sc = score(words, title, also);
    if (sc > 0) hits.push({ key: `${kind}:${key}`, kind, title, score: sc, ...rest });
  };
  const now = today();

  for (const p of s.pages) {
    add("page", p.href, p.label, p.keywords ?? [], { href: p.href, detail: p.detail });
  }
  for (const i of s.items ?? []) {
    if (i.archived) continue;
    add("item", i.id, i.displayName, itemTerms(i), {
      href: `/items/${i.id}`,
      detail: [categoryLabel(i.category), formLabel(i)].filter(Boolean).join(" · "),
    });
  }
  for (const r of s.recipes ?? []) {
    add("recipe", r.id, r.name, [r.style?.name, r.brewer, r.description], {
      href: `/recipes/${r.id}`,
      detail: [r.style?.name, r.version > 1 ? `version ${r.version}` : null]
        .filter(Boolean)
        .join(", "),
    });
  }
  for (const b of s.brews ?? []) {
    // A planned brew has no number until its brew day starts, so it is found and shown without one.
    add(
      "brew",
      b.id,
      b.recipe.name,
      b.number === null ? [] : [`brew ${b.number}`, `#${b.number}`],
      {
        href: `/brews/${b.id}`,
        detail: b.number === null ? brewDetail(b) : `Brew ${b.number}, ${brewDetail(b)}`,
        order: brewOrder(b, now),
      },
    );
  }
  for (const inv of s.invoices ?? []) {
    const title = [inv.supplierName, inv.invoiceNumber ? `#${inv.invoiceNumber}` : null]
      .filter(Boolean)
      .join(" ");
    add(
      "invoice",
      inv.id,
      title || inv.file?.filename || "Invoice",
      [inv.invoiceNumber, inv.orderNumber, inv.customerRef, inv.file?.filename, "invoice"],
      {
        href: `/invoices/${inv.id}`,
        detail: [showDate(inv.invoiceDate), INVOICE_STATUS[inv.status]].filter(Boolean).join(", "),
      },
    );
  }
  for (const l of s.locations ?? []) {
    if (l.archived) continue;
    add("location", l.id, l.name, [], { href: "/", locationId: l.id, detail: "Show what is here" });
  }
  for (const name of s.breweries ?? []) {
    add("brewery", name, name, [], { href: "/borrowing", detail: "Borrowing" });
  }
  for (const b of s.brewSystems ?? []) {
    add("brewSystem", b.id, b.name, ["brew system"], {
      href: "/settings#brew-systems",
      detail: "Settings",
    });
  }
  for (const d of s.drafts ?? []) {
    add("draft", d.id, d.name || "Untitled recipe", ["draft"], {
      href: `/recipes/builder?draft=${encodeURIComponent(d.id)}`,
      detail: `Saved ${showDate(d.savedAt)}`,
    });
  }
  for (const r of s.ollieReports ?? []) {
    const title = r.reportDate ? `Ollie report of ${showDate(r.reportDate)}` : r.filename;
    add("ollie", r.id, title, [r.filename, "ollie"], {
      href: `/settings/ollie/${r.id}`,
      detail: { draft: "To check", imported: "Imported", undone: "Undone" }[r.status],
    });
  }

  return KINDS.flatMap(({ kind }) =>
    hits
      .filter((h) => h.kind === kind)
      .sort(
        (a, b) =>
          b.score - a.score || (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title),
      )
      .slice(0, PER_KIND),
  );
}
